import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { copyLink, seriesPublicUrl } from '../utils/publicLinks';
import { uploadEventPoster } from '../utils/imageApi';
import { convertToMoney } from '../utils/formats';
import {
    getSeries, newSession,
    ESTADO_JORNADA, weekdayLabel, formatStartTime, formatEventDateTime, formatYMD,
} from '../utils/seriesApi';

// La pantalla de la serie: jornada por jornada con cupo, anotados, ingresaron, recaudado, a
// cobrar y mesas, más los totales y el promedio de la serie; las que ya pasaron se ven
// "Jugada"; "Nueva jornada"; y, cuando la serie todavía no tiene ninguna, el form
// de primera jornada (FR-003, FR-005, FR-008, FR-009, FR-011).
//
// TC-002: acá NO se calcula ninguna fecha. `businessDate` (la jornada del salón de hoy), el
// "jugada" de cada fila y la fecha que le toca a la jornada nueva los decide el backend —
// el navegador sólo los muestra. Por eso "Nueva jornada" manda el body vacío y no propone
// fecha, y el form de primera jornada tampoco la pide.
//
// ⚠️ Los dos casings del contrato conviven en esta pantalla: `series` es la fila del modelo
// (snake_case: `start_time`, `poster_url`, `branch_id`, `active`, `notes`) y las jornadas de
// `events[]` vienen calculadas, en camelCase (`startsAt`, `tablesReserved`, `attendees`).
const TICKET_KINDS = [
    { value: 'general', label: 'General' },
    { value: 'early_bird', label: 'Anticipada (early bird)' },
    { value: 'member', label: 'Socio' },
    { value: 'free', label: 'Libre (gratis)' },
];

const money = (value) => `$ ${convertToMoney(Number(value) || 0)}`;

const PRIMERA_VACIA = {
    title: '',
    capacity: '',
    ticketName: 'General',
    ticketKind: 'general',
    ticketPrice: '',
    // null = "todavía no la tocaron": se muestra la de la serie (FR-008).
    description: null,
    posterUrl: '',
    posterFile: null,
    posterPreview: '',
};

class SeriesDetailPage extends Component {

    constructor() {
        const cachedRole = Number(localStorage.getItem('role') || 0);
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: cachedRole,
            id: null,
            loading: true,
            error: null,
            series: null,
            businessDate: null,
            nextEvent: null,
            events: [],
            totals: null,
            averages: null,
            trabajando: false,
            aviso: null,
            primera: { ...PRIMERA_VACIA },
            subiendoPoster: false,
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        const id = this.props.match.params.id;
        this.setState({ checked: true, role, id });
        this.load(id);
    }

    load = async (id = this.state.id) => {
        this.setState({ loading: true, error: null });
        try {
            const data = await getSeries(id);
            this.setState({
                series: (data && data.series) || null,
                businessDate: (data && data.businessDate) || null,
                // Ojo: `nextEvent` se decide con la fecha de CALENDARIO y `businessDate` con
                // la jornada del salón. A las 00:30 de una noche larga discrepan un día: son
                // dos datos distintos y ninguno se deriva del otro.
                nextEvent: (data && data.nextEvent) || null,
                events: (data && data.events) || [],
                totals: (data && data.totals) || null,
                averages: (data && data.averages) || null,
                loading: false,
            });
        } catch (e) {
            this.setState({ error: e.message, loading: false });
        }
    }

    setPrimera = (campo, valor) => {
        this.setState((prev) => ({ primera: { ...prev.primera, [campo]: valor }, aviso: null }));
    }

    // ── Nueva jornada (FR-005) ────────────────────────────────────────────────────────────
    //
    // Body vacío: la fecha la pone la cadencia de la serie sobre la última jornada, el título
    // se propone solo y los tipos de entrada se copian. La jornada nace en borrador y se abre
    // para revisarla y publicarla. Molde: TableEventsComponent.duplicar.
    nuevaJornada = async () => {
        if (!window.confirm('¿Agregar la próxima jornada? Se copia la última (tipos de entrada incluidos) a la fecha que toca, en borrador.')) return;
        this.setState({ trabajando: true, aviso: null });
        try {
            const data = await newSession(this.state.id, {});
            navigateTo(`/editEvent?id=${data.event.id}`);
        } catch (e) {
            this.setState({ trabajando: false });
            alert(e.message);
        }
    }

    // ── Primera jornada (FR-008) ──────────────────────────────────────────────────────────
    subirPoster = async () => {
        const file = this.state.primera.posterFile;
        if (!file) return null;
        this.setState({ subiendoPoster: true });
        try {
            const subido = await uploadEventPoster(file, file.name);
            this.setState((prev) => ({
                subiendoPoster: false,
                primera: { ...prev.primera, posterUrl: subido.url, posterPreview: subido.url, posterFile: null },
            }));
            return subido.url;
        } catch (e) {
            this.setState({ subiendoPoster: false });
            alert(`No se pudo subir el póster: ${e.message}`);
            return null;
        }
    }

    crearPrimeraJornada = async () => {
        const { primera } = this.state;
        const precio = String(primera.ticketPrice).trim();
        if (!primera.ticketName.trim()) { alert('Poné el nombre del tipo de entrada.'); return; }
        if (precio === '') { alert('Poné el precio de la entrada (0 si es gratis).'); return; }
        // El backend rechaza los centavos porque `product_purchase.price` es INT: avisar acá
        // evita perder el formulario entero por un punto de más.
        if (!Number.isInteger(Number(precio)) || Number(precio) < 0) {
            alert('El precio va en pesos enteros, sin centavos.');
            return;
        }
        let posterUrl = primera.posterUrl;
        if (primera.posterFile) {
            posterUrl = await this.subirPoster();
            if (!posterUrl) return;
        }
        this.setState({ trabajando: true, aviso: null });
        try {
            const data = await newSession(this.state.id, {
                title: primera.title.trim() || undefined,
                capacity: String(primera.capacity).trim() === '' ? undefined : parseInt(primera.capacity, 10),
                ticketType: {
                    name: primera.ticketName.trim(),
                    kind: primera.ticketKind,
                    price: Number(precio),
                },
                // Sin póster propio hereda el de la serie. La descripción se OMITE mientras
                // nadie la toque: el backend hereda la de la serie sola, y mandar '' la
                // guardaría vacía (FR-008).
                ...(posterUrl ? { posterUrl } : {}),
                ...(primera.description == null ? {} : { description: primera.description }),
            });
            navigateTo(`/editEvent?id=${data.event.id}`);
        } catch (e) {
            this.setState({ trabajando: false });
            alert(e.message);
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────────────────
    renderFila = (jornada, index) => {
        // Una jornada que ya pasó y sigue publicada se ve "Jugada": no pide que la cierren (la
        // venta ya cortó sola por la hora). Lo decide el backend (`played`), no el reloj de acá.
        const estado = jornada.played
            ? { label: 'Jugada', badge: 'badge-light border' }
            : (ESTADO_JORNADA[jornada.status] || { label: jornada.status, badge: 'badge-light' });
        const att = jornada.attendees || { confirmed: 0, pending: 0, redeemed: 0 };
        // Cuál es "la próxima" lo dice el backend (`nextEvent`), no una comparación de fechas
        // acá: se decide por la fecha de CALENDARIO de allá, que de madrugada no coincide con
        // la jornada del salón (TC-002).
        const proxima = this.state.nextEvent && Number(this.state.nextEvent.id) === Number(jornada.id);
        return (
            <tr role="row" key={jornada.id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                <td className="text-left">
                    <div>
                        {formatEventDateTime(jornada.startsAt)}
                        {proxima && <span className="badge badge-info ml-2">Próxima</span>}
                    </div>
                    <small className="text-muted">{jornada.title}</small>
                </td>
                <td>
                    {jornada.sold}{jornada.capacity != null ? `/${jornada.capacity}` : ''}
                    {jornada.capacity == null && <small className="text-muted"> sin cupo</small>}
                    {jornada.full && <span className="badge badge-warning ml-2">Completo</span>}
                </td>
                <td>
                    {att.confirmed}
                    {att.pending > 0 && <small className="text-muted"> (+{att.pending} pend.)</small>}
                </td>
                <td>{att.redeemed}</td>
                <td>{money(jornada.revenue)}</td>
                <td>{Number(jornada.receivable) > 0 ? <span className="text-warning">{money(jornada.receivable)}</span> : money(0)}</td>
                <td>{jornada.tablesReserved}</td>
                <td>
                    <span className={`badge ${estado.badge}`}>{estado.label}</span>
                </td>
                <td className="text-right text-nowrap">
                    <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo(`/editEvent?id=${jornada.id}`)}>
                        Abrir
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary ml-2" onClick={() => navigateTo(`/eventAdmissions?id=${jornada.id}`)}>
                        Anotados
                    </button>
                </td>
            </tr>
        );
    }

    // Totales de TODAS las jornadas y promedio de las ya jugadas. Con cero jugadas el promedio
    // es "—" y NUNCA 0: "todavía no hay de qué promediar" no es "el promedio da cero", y la
    // jornada que se acaba de crear hundiría el número justo el día en que se la mira.
    // `averages` NO trae `receivable` ni `tablesReserved`: esas dos celdas quedan vacías.
    renderResumen = () => {
        const { totals, averages } = this.state;
        if (!totals) return null;
        const sinJugadas = !averages || Number(averages.over) === 0;
        const prom = (valor) => (sinJugadas || valor == null ? '—' : valor);
        // El tema estiliza sólo `tbody td` de .transaction-history: sin esto el pie queda con
        // 1px de padding y alineado a la izquierda, fuera de las columnas de arriba.
        const celda = { padding: '7px 17px', textAlign: 'center', borderTop: '1px solid #e1e5eb' };
        const rotulo = { ...celda, textAlign: 'left' };
        return (
            <tfoot>
                <tr style={{ background: '#f5f6f8', fontWeight: 600 }}>
                    <td style={rotulo}>Total · {totals.events} {totals.events === 1 ? 'jornada' : 'jornadas'}</td>
                    <td style={celda}></td>
                    <td style={celda}>{totals.attendees}</td>
                    <td style={celda}>{totals.redeemed}</td>
                    <td style={celda}>{money(totals.revenue)}</td>
                    <td style={celda}>{money(totals.receivable)}</td>
                    <td style={celda}>{totals.tablesReserved}</td>
                    <td style={celda} colSpan="2"></td>
                </tr>
                <tr style={{ background: '#fafbfc' }}>
                    <td style={rotulo} className="text-muted">
                        {sinJugadas
                            ? 'Promedio — todavía no se jugó ninguna'
                            : `Promedio sobre ${averages.over} ${Number(averages.over) === 1 ? 'jornada jugada' : 'jornadas jugadas'}`}
                    </td>
                    <td style={celda}></td>
                    <td style={celda}>{prom(averages && averages.attendees)}</td>
                    <td style={celda}>{prom(averages && averages.redeemed)}</td>
                    <td style={celda}>{sinJugadas || averages.revenue == null ? '—' : money(averages.revenue)}</td>
                    <td style={celda} colSpan="4"></td>
                </tr>
            </tfoot>
        );
    }

    renderJornadas = () => {
        const events = this.state.events;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                    <h6 className="m-0 mr-auto">Jornadas</h6>
                    {/* La jornada del salón de hoy la dice el backend (`businessDate`): a las
                        00:30 de una noche larga sigue siendo la de ayer, y es contra ésta que
                        se decide qué jornada ya se jugó. El navegador no la deriva (TC-002). */}
                    {this.state.businessDate && (
                        <small className="text-muted">Jornada del salón de hoy: {formatYMD(this.state.businessDate)}</small>
                    )}
                </div>
                <div className="card-body p-0 pb-3">
                    <div className="dataTables_wrapper no-footer">
                        <table className="transaction-history dataTable no-footer dtr-inline" role="grid">
                            <thead>
                                <tr role="row">
                                    <th className="text-left" style={{ width: '20%' }}>Fecha</th>
                                    <th style={{ width: '10%' }}>Cupo</th>
                                    <th style={{ width: '9%' }}>Anotados</th>
                                    <th style={{ width: '9%' }}>Ingresaron</th>
                                    <th style={{ width: '10%' }}>Recaudado</th>
                                    <th style={{ width: '10%' }}>A cobrar</th>
                                    <th style={{ width: '7%' }}>Mesas</th>
                                    <th style={{ width: '12%' }}>Estado</th>
                                    <th style={{ width: '13%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.length
                                    ? events.map(this.renderFila)
                                    : (<tr><td colSpan="9" className="text-center" style={{ padding: 30, color: '#818EA3' }}>
                                        Esta serie todavía no tiene jornadas.
                                    </td></tr>)}
                            </tbody>
                            {events.length ? this.renderResumen() : null}
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // FR-008: la primera jornada no se puede duplicar de ninguna, así que se pide lo mínimo —
    // título (o el propuesto), cupo y el tipo de entrada inicial. La FECHA no se pide: la pone
    // la cadencia de la serie (TC-002). La sucursal y la visibilidad tampoco: son de la serie.
    renderPrimeraJornada = () => {
        const { series, primera } = this.state;
        const descripcion = primera.description == null ? (series.description || '') : primera.description;
        const posterPreview = primera.posterPreview || series.poster_url || '';
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Primera jornada</h6>
                </div>
                <div className="card-body">
                    <p className="text-muted">
                        La fecha la pone la cadencia de la serie ({weekdayLabel(series.weekday)} a las {formatStartTime(series.start_time)}):
                        se agenda la primera que todavía no pasó. La sucursal, la visibilidad y el póster salen de la serie.
                        De acá en adelante alcanza con "Nueva jornada".
                    </p>
                    <div className="row">
                        <div className="col-md-6 form-group">
                            <label>Título</label>
                            <input type="text" className="form-control" value={primera.title}
                                placeholder={`${series.name} · dd/mm`}
                                onChange={(e) => this.setPrimera('title', e.target.value)} />
                            <small className="text-muted">Vacío = el propuesto, con la fecha que le toque.</small>
                        </div>
                        <div className="col-md-3 form-group">
                            <label>Cupo</label>
                            <input type="number" className="form-control" value={primera.capacity}
                                onChange={(e) => this.setPrimera('capacity', e.target.value)} />
                            <small className="text-muted">Vacío = sin tope.</small>
                        </div>
                    </div>

                    <h6 className="text-uppercase text-muted">Entrada inicial *</h6>
                    <div className="row">
                        <div className="col-md-4 form-group">
                            <label>Nombre</label>
                            <input type="text" className="form-control" value={primera.ticketName}
                                onChange={(e) => this.setPrimera('ticketName', e.target.value)} />
                        </div>
                        <div className="col-md-3 form-group">
                            <label>Tipo</label>
                            <select className="form-control" value={primera.ticketKind}
                                onChange={(e) => this.setPrimera('ticketKind', e.target.value)}>
                                {TICKET_KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
                            </select>
                        </div>
                        <div className="col-md-3 form-group">
                            <label>Precio *</label>
                            <input type="number" step="1" min="0" className="form-control" value={primera.ticketPrice}
                                onChange={(e) => this.setPrimera('ticketPrice', e.target.value)} />
                            <small className="text-muted">Pesos enteros, sin centavos.</small>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Descripción</label>
                        <textarea className="form-control" rows="2" value={descripcion}
                            onChange={(e) => this.setPrimera('description', e.target.value)} />
                        <small className="text-muted">Viene de la serie; se puede cambiar para esta jornada.</small>
                    </div>

                    <div className="form-row">
                        <div className="form-group col-md-6">
                            <label className="d-block">Póster</label>
                            {posterPreview && (<div className="mb-2"><img src={posterPreview} alt="Póster de la jornada" width="120" /></div>)}
                            <input type="file" accept="image/*" onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                this.setState((prev) => ({
                                    primera: { ...prev.primera, posterFile: file, posterPreview: URL.createObjectURL(file) },
                                }));
                            }} />
                            <small className="d-block text-muted mt-2">Vacío = el de la serie. Si elegís otro, se sube al crear la jornada.</small>
                        </div>
                    </div>

                    <button type="button" className="btn btn-accent"
                        disabled={this.state.trabajando || this.state.subiendoPoster || !series.active}
                        onClick={this.crearPrimeraJornada}>
                        {this.state.trabajando || this.state.subiendoPoster ? 'Creando…' : 'Crear la primera jornada'}
                    </button>
                    {!series.active && (
                        <small className="d-block text-muted mt-2">La serie está inactiva: activala para agregarle jornadas.</small>
                    )}
                </div>
            </div>
        );
    }

    renderFicha = () => {
        const series = this.state.series;
        return (
            <div className="card card-small mb-4">
                <div className="card-body py-3">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                        <span><b>{weekdayLabel(series.weekday)} {formatStartTime(series.start_time)}</b></span>
                        <span className={`badge ${series.active ? 'badge-success' : 'badge-secondary'}`}>
                            {series.active ? 'Activa' : 'Inactiva'}
                        </span>
                        {series.visibility === 'private' && (
                            <span className="badge badge-secondary" title="Las jornadas no se listan en la web">Privada</span>
                        )}
                        {this.state.totals && (
                            <span className="text-muted">
                                {this.state.totals.events} {this.state.totals.events === 1 ? 'jornada' : 'jornadas'}
                            </span>
                        )}
                    </div>
                    {series.notes && (
                        <p className="text-muted mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                            <b>Notas internas:</b> {series.notes}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) return (<div></div>);
        const { series } = this.state;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={51} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                {/* minWidth:0 — el header es un .row de Bootstrap y colapsa contra
                                    los botones: el nombre de la serie se partía en varias líneas. */}
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <HeaderComponent title="Series del club" subtitle={series ? series.name : 'Serie'} />
                                </div>
                                <div className="text-nowrap" style={{ flex: '0 0 auto' }}>
                                    <button type="button" className="btn btn-white" onClick={() => navigateTo('/series')}>
                                        Volver a series
                                    </button>
                                    {/* El link único de la serie: el que se comparte para que cada
                                        uno elija a qué jornada se suma (/club/<slug>). */}
                                    {series && series.slug && (
                                        <button type="button" className="btn btn-white ml-2"
                                            title={seriesPublicUrl(series.slug)}
                                            onClick={() => copyLink(seriesPublicUrl(series.slug))}>
                                            Copiar link de la serie
                                        </button>
                                    )}
                                    {/* FR-009: una serie inactiva no ofrece "Nueva jornada". Y sin
                                        jornadas previas no hay de dónde duplicar: ahí manda el
                                        form de primera jornada de abajo. */}
                                    {series && series.active && this.state.events.length > 0 && (
                                        <button type="button" className="btn btn-accent ml-2"
                                            disabled={this.state.trabajando} onClick={this.nuevaJornada}>
                                            + Nueva jornada
                                        </button>
                                    )}
                                </div>
                            </div>

                            {this.state.aviso ? (
                                <div className="alert alert-success" role="alert">{this.state.aviso}</div>
                            ) : null}

                            {this.state.loading && (
                                <div className="card card-small mb-4"><div className="card-body text-center" style={{ padding: 30 }}>Cargando…</div></div>
                            )}

                            {!this.state.loading && this.state.error && (
                                <div className="alert alert-danger" role="alert">{this.state.error}</div>
                            )}

                            {!this.state.loading && !this.state.error && series && (
                                <>
                                    {this.renderFicha()}
                                    {this.renderJornadas()}
                                    {this.state.events.length === 0 && this.renderPrimeraJornada()}
                                </>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default SeriesDetailPage;
